import { Translation } from '@app/api/translation.api';
import { Popconfirm } from '@app/components/common/Popconfirm/Popconfirm';
import { PermissionTypes } from '@app/constants/enums/permission';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { useLoader } from '@app/hooks/useLoader';
import { useMounted } from '@app/hooks/useMounted';
import { ANY_OBJECT } from '@app/interfaces/interfaces';
import { addNewLanguage, doUpdateTranslation, getTranslationList } from '@app/store/slices/translationsSlice';
import { capitalize, getDefaultTranslationRow, getPermissionType, sortAscendingDescending } from '@app/utils/utils';
import { Form, Space, TablePaginationConfig } from 'antd';
import { BasicTableRow, Pagination } from 'api/table.api';
import { FilterIcon } from 'components/common/icons/FilterIcon';
import { Table } from 'components/common/Table/Table';
import { flatMap, get, isObject, set } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EditableCell } from '../editableTable/EditableCell';
import * as S from './TranslationTable.styles';
import { EditIcon } from '@app/components/common/icons/EditIcon';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';

export const TranslationTable: React.FC = () => {
  const [form] = Form.useForm();
  const translation = useAppSelector((state) => state.translation);
  const [initialPagination, setInitialPagination] = useState<Pagination>({
    current: 1,
    pageSize: 10,
  });
  const [tableData, setTableData] = useState<{ data: BasicTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const [filteredTableData, setFilteredTableData] = useState<{
    data: BasicTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const [editingKey, setEditingKey] = useState(0);
  const { handleLoaderOpen, handleLoaderClose } = useLoader();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('search'));
  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [permission, setPermission] = useState(0);
  const userPermission = useAppSelector((state) => state.user.user?.role?.permissions);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setInitialPagination(pagination);
    fetch(pagination);
    cancel();
  };

  const isEditing = (record: BasicTableRow) => record.key === editingKey;

  const edit = (record: Partial<BasicTableRow> & { key: React.Key }) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey(0);
  };

  const cancelPermission = () => {
    setEditingKey(0);
  };

  const save = async (key: React.Key) => {
    handleLoaderOpen();
    try {
      const row = (await form.validateFields()) as BasicTableRow;

      const newData = [...tableData.data];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
      } else {
        newData.push(row);
      }
      const changes = translation?.translationList.reduce((acc: ANY_OBJECT, curr: Translation) => {
        const lang = curr.name;
        let changed = false;
        let translations: ANY_OBJECT = curr.translations ? JSON.parse(JSON.stringify(curr.translations)) : {};
        newData.forEach((field) => {
          const value = get(translations, field.key);
          const newVal = field[lang];
          if (newVal !== value) {
            translations = set(translations, field.key, newVal ? newVal : '');
            changed = true;
          }
        });
        if (changed)
          acc = acc.concat({
            ...curr,
            translations,
          });

        return acc;
      }, []);
      changes.forEach((change: Translation, index: number) => {
        if (change.id) {
          dispatch(doUpdateTranslation(change))
            .unwrap()
            .then(async () => {
              if (index === changes.length - 1) {
                notificationController.success({
                  message: t('common.success'),
                });
                setEditingKey(0);
                await dispatch(getTranslationList());
                handleLoaderClose();
              }
            })
            .catch((err) => {
              notificationController.error({ message: err.message });
            });
        } else {
          dispatch(addNewLanguage(change))
            .unwrap()
            .then(async () => {
              if (index === changes.length - 1) {
                notificationController.success({
                  message: t('common.success'),
                });
                setEditingKey(0);
                await dispatch(getTranslationList());
                handleLoaderClose();
              }
            })
            .catch((err) => {
              notificationController.error({ message: err.message });
            });
        }
      });
    } catch (errInfo) {}
  };

  const columns = [
    {
      title: t('tables.key'),
      dataIndex: 'key',
      width: 'auto',
      editable: false,
      defaultSortOrder: 'descend' as const,
      sorter: (a: ANY_OBJECT, b: ANY_OBJECT) => sortAscendingDescending(a.key, b.key),
    },
    ...translation?.translationList.map((item: { name: string }) => ({
      title: capitalize(item.name),
      dataIndex: item.name,
      width: 'auto',
      editable: true,
      defaultSortOrder: 'descend' as const,
      sorter: (a: ANY_OBJECT, b: ANY_OBJECT) => {
        if (typeof a[item.name] !== 'string' && typeof b[item.name] !== 'string') return 0;
        if (typeof a[item.name] !== 'string') return -1;
        if (typeof b[item.name] !== 'string') return 1;
        return sortAscendingDescending(a[item.name], b[item.name]);
      },
    })),
    {
      title: t('tables.actions'),
      dataIndex: 'actions',
      width: 'auto',
      render: (text: string, record: BasicTableRow) => {
        const editable = isEditing(record);
        return (
          <Space>
            {editable ? (
              <>
                <S.ActionButton title={t('common.save')} onClick={() => save(record.key)}>
                  <SaveOutlined />
                </S.ActionButton>
                <Popconfirm
                  title={t('tables.cancelInfo')}
                  onConfirm={cancelPermission}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                >
                  <S.ActionButton>
                    <CloseOutlined />
                  </S.ActionButton>
                </Popconfirm>
              </>
            ) : (
              <S.ActionButton title={t('common.edit')} disabled={editingKey !== 0} onClick={() => edit(record)}>
                <EditIcon />
              </S.ActionButton>
            )}
          </Space>
        );
      },
    },
  ];

  const mergedColumns = () => {
    const mergedCol = columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: BasicTableRow) => ({
          record,
          inputType: 'text',
          dataIndex: col.dataIndex,
          title: capitalize(col.title),
          editing: isEditing(record),
        }),
      };
    });

    if (permission != PermissionTypes.READWRITE) {
      const tableActionToRemove = mergedCol.findIndex((column) => column.dataIndex === 'actions');
      if (tableActionToRemove >= 0) {
        mergedCol.splice(tableActionToRemove, 1);
      }
    }

    return mergedCol;
  };

  const getSchema = useCallback(
    (val: ANY_OBJECT, keys = []): ANY_OBJECT =>
      isObject(val) ? flatMap(val, (v, k) => getSchema(v, [...keys, k])) : keys.join('.'),
    [],
  );

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      const translations = translation?.translationList as Translation[];
      const defaultTranslation = getDefaultTranslationRow();
      const schema = getSchema(defaultTranslation.translations as unknown as ANY_OBJECT);
      const data: ANY_OBJECT = schema.reduce(
        (acc: ANY_OBJECT, curr: string) => ({ ...acc, [curr]: { key: curr } }),
        {},
      );
      translations.map((translation) => {
        schema.map((row: string) => {
          data[row][translation.name as string] = get(translation.translations, row);
          if (!data[row][translation.name as string] && translation.name && translation.isoCode === 'en') {
            data[row][translation.name as string] = get(defaultTranslation.translations, row);
          }
        });
      });
      const res = Object.keys(data).map((row): string => data[row]);
      if (isMounted.current) {
        setTableData({
          data: res as unknown as BasicTableRow[],
          pagination: { ...pagination, total: res.length },
          loading: false,
        });
      }
    },
    [getSchema, isMounted, translation?.translationList],
  );

  const setQueryParam = (value: string) => {
    const params = new URLSearchParams({ search: value || '' });
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    setQuery(value);
  };

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch, initialPagination]);

  useEffect(() => {
    if (query) {
      const data = [...tableData.data];
      const filtered = data.filter((item) => {
        return (
          Object.values(item).filter((str) => {
            return str && str.toString().toLowerCase().indexOf(query.toLowerCase()) >= 0;
          }).length > 0
        );
      });
      setFilteredTableData((tableData) => ({
        ...tableData,
        data: filtered,
        pagination: { total: filtered.length },
      }));
    }
  }, [location.pathname, navigate, query, tableData.data]);

  useEffect(() => {
    if (userPermission) {
      const permission = getPermissionType(location, params, userPermission);
      setPermission(permission);
    }
  }, [location, params, userPermission]);

  return (
    <Form form={form} component={false}>
      <S.TableActionWrapper className="flex-start">
        <S.InputSearch
          defaultValue={query || ''}
          placeholder={t('header.search')}
          onChange={(event) => setQueryParam(event.target.value)}
          filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
          allowClear
          enterButton={null}
          addonAfter={null}
        />
      </S.TableActionWrapper>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        dataSource={query ? filteredTableData.data : tableData.data}
        columns={mergedColumns()}
        rowClassName="editable-row"
        pagination={{
          ...(query ? filteredTableData.pagination : tableData.pagination),
          onChange: cancel,
          locale: { items_per_page: `/ ${t('common.page')}` },
        }}
        onChange={handleTableChange}
        loading={query ? filteredTableData.loading : tableData.loading}
        scroll={{ x: 800 }}
        locale={{ emptyText: t('common.noData') }}
      />
    </Form>
  );
};
